export type GoogleCredentialResponse = {
  credential?: string;
  select_by?: string;
};

export type GooglePromptMomentNotification = {
  isNotDisplayed(): boolean;
  isSkippedMoment(): boolean;
};

type GoogleIdentityConfiguration = {
  client_id: string;
  callback: (response: GoogleCredentialResponse) => void;
  auto_select: false;
  use_fedcm_for_prompt: true;
  use_fedcm_for_button: true;
  cancel_on_tap_outside: true;
};

type GoogleIdentityApi = {
  initialize(options: GoogleIdentityConfiguration): void;
  renderButton(
    parent: HTMLElement,
    options: {
      type: 'standard';
      theme: 'outline';
      size: 'large';
      text: 'continue_with';
    },
  ): void;
  prompt(callback: (notification: GooglePromptMomentNotification) => void): void;
  cancel(): void;
  disableAutoSelect(): void;
};

declare global {
  interface Window {
    google?: {
      accounts: {
        id: GoogleIdentityApi;
      };
    };
  }
}

const GOOGLE_IDENTITY_SCRIPT_URL = 'https://accounts.google.com/gsi/client';
let googleIdentityScriptPromise: Promise<void> | undefined;
let activeGoogleIdentityOwner: symbol | undefined;

export function loadGoogleIdentityScript(): Promise<void> {
  if (window.google?.accounts?.id) {
    return Promise.resolve();
  }
  if (googleIdentityScriptPromise) {
    return googleIdentityScriptPromise;
  }

  googleIdentityScriptPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src="${GOOGLE_IDENTITY_SCRIPT_URL}"]`,
    );
    const script = existingScript ?? document.createElement('script');

    const handleLoad = () => {
      removeListeners();
      resolve();
    };
    const handleError = () => {
      removeListeners();
      script.remove();
      googleIdentityScriptPromise = undefined;
      reject(new Error('Unable to load Google Identity Services.'));
    };
    const removeListeners = () => {
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
    };

    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });

    if (!existingScript) {
      script.src = GOOGLE_IDENTITY_SCRIPT_URL;
      script.async = true;
      document.head.append(script);
    }
  });

  return googleIdentityScriptPromise;
}

export function initializeGoogleIdentity(options: {
  clientId: string;
  buttonElement: HTMLElement;
  onCredential: (credential: string, source: 'button' | 'one_tap') => void;
  onPromptUnavailable: () => void;
}): () => void {
  const googleIdentity = window.google?.accounts?.id;
  if (!googleIdentity) {
    throw new Error('Google Identity Services is unavailable.');
  }

  if (activeGoogleIdentityOwner) {
    throw new Error('Google Identity Services already has an active owner.');
  }

  const owner = Symbol('google-identity-owner');
  activeGoogleIdentityOwner = owner;
  let active = true;
  try {
    googleIdentity.initialize({
      client_id: options.clientId,
      callback: (response) => {
        if (!active || !response.credential) return;
        const source =
          response.select_by === 'btn' || response.select_by === 'btn_confirm'
            ? 'button'
            : 'one_tap';
        options.onCredential(response.credential, source);
      },
      auto_select: false,
      use_fedcm_for_prompt: true,
      use_fedcm_for_button: true,
      cancel_on_tap_outside: true,
    });
    googleIdentity.renderButton(options.buttonElement, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: 'continue_with',
    });
    googleIdentity.prompt((notification) => {
      if (!active) return;
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        options.onPromptUnavailable();
      }
    });
  } catch (error) {
    active = false;
    if (activeGoogleIdentityOwner === owner) {
      activeGoogleIdentityOwner = undefined;
    }
    throw error;
  }

  return () => {
    if (!active) return;
    active = false;
    if (activeGoogleIdentityOwner !== owner) return;
    activeGoogleIdentityOwner = undefined;
    googleIdentity.cancel();
  };
}

export function disableGoogleAutoSelect(): void {
  window.google?.accounts?.id.disableAutoSelect();
}
